import React from 'react'
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle'
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek'
import EventIcon from '@mui/icons-material/Event'

// Mirrors server/src/helpers/scheduling/shiftIdentity.js getShiftListType -
// shiftType defaults to 'Event', and 'Admin'/'FrontLine' are both roster-plan
// shifts surfaced as 'Rota'. Rows from /available-shifts already carry
// `listType`; everything else (e.g. application rows) is derived here.
export const getShiftListType = (shift) => {
  if (shift?.listType) return shift.listType
  if (shift?.shiftType === 'PTS' || shift?.ptsDuty) return 'PTS'
  if (shift?.shiftType === 'Event') return 'Event'
  return 'Rota'
}

export const SHIFT_TYPE_FILTERS = [
  { value: 'All', label: 'All' },
  { value: 'Event', label: 'Events', icon: <EventIcon fontSize="small" /> },
  {
    value: 'PTS',
    label: 'PTS',
    icon: <AirportShuttleIcon fontSize="small" />,
  },
  {
    value: 'Rota',
    label: 'Rota',
    icon: <CalendarViewWeekIcon fontSize="small" />,
  },
]

// Palette keys (theme.palette[key].main) for the type chips.
export const SHIFT_TYPE_CHIP_COLOURS = {
  Event: 'anBlue',
  PTS: 'success',
  Rota: 'secondary',
}

export const SHIFT_TYPE_LABELS = {
  Event: 'Event',
  PTS: 'Patient Transport',
  Rota: 'Rota',
}

// What a board row is called: the event, the PTS duty, or the rota plan.
export const getShiftListTitle = (shift) => {
  if (shift?.event?.title) return shift.event.title
  const type = getShiftListType(shift)
  if (type === 'PTS') {
    return shift?.ptsDuty?.dutyRef
      ? `PTS duty ${shift.ptsDuty.dutyRef}`
      : 'Patient Transport duty'
  }
  if (shift?.roster?.title) return shift.roster.title
  return type === 'Rota' ? 'Rota shift' : 'Shift'
}

// /available-shifts summarises the duty (see summarisePtsDuty on the server);
// other endpoints may hand the popup a raw populated duty or a bare id, so
// read defensively and say what we can.
export const describePtsDuty = (duty) => {
  if (!duty || typeof duty !== 'object') return null

  const vehicle = duty.vehicle
  const vehicleLabel = vehicle?.callSign
    ? [vehicle.callSign, vehicle.regPlate].filter(Boolean).join(' · ')
    : vehicle?.regPlate
      ? vehicle.regPlate
      : vehicle
        ? 'Vehicle assigned'
        : 'No vehicle assigned'

  const customer = duty.customer
  const customerLabel =
    customer?.label ?? duty.organisation?.orgName ?? 'Patient is the customer'
  let customerCaption = null
  if (customer?.dedicated) {
    customerCaption = 'Dedicated duty for this organisation'
  } else if (customer?.kind === 'organisation' && customer?.patientFunded) {
    customerCaption = 'Plus patient-funded journeys'
  } else if (customer?.kind === 'patient') {
    customerCaption = customer.journeyCount
      ? `${customer.journeyCount} journey${customer.journeyCount === 1 ? '' : 's'} planned`
      : 'No journeys planned yet'
  }

  return {
    dutyRef: duty.dutyRef ?? null,
    baseLocationName: duty.baseLocation?.name ?? null,
    hasVehicle: !!vehicle,
    vehicleLabel,
    customerLabel,
    customerCaption,
  }
}
